/**
 * KinsCircle Phase 6D.2 — Generation Handoff Card.
 *
 * STABILITY.md §13–§14:
 *   - READ-ONLY surface that highlights cross-generation continuity:
 *     traditions, recurring family events, and themes that span ≥20
 *     years of the family's saved memories.
 *   - NEVER infers emotional relationships, NEVER guesses lineage gaps,
 *     NEVER invents events. Every claim is anchored to ≥3 memories
 *     spanning ≥2 generations of saved history.
 *   - Renders null when `generationHandoff` flag is OFF or no qualifying
 *     traditions exist.
 *   - Mandatory citation per row.
 */

import React, { useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  GitBranch,
  PlayCircle,
  Calendar,
  Sparkles,
} from 'lucide-react';
import {
  isGenerationHandoffEnabled,
  isStoryPlaybackEnabled,
} from '@/lib/ai/featureFlag';
import {
  buildGenerationCollection,
  collectionToPlaybackSlides,
  type LegacyCollection,
} from '@/lib/ai/legacyCollectionEngine';
import StoryPlaybackModal, { type PlaybackStory } from './StoryPlaybackModal';

const formatYearRange = (r: { from: number | null; to: number | null }): string => {
  if (r.from && r.to) return `${r.from}–${r.to}`;
  if (r.from) return `${r.from}`;
  if (r.to) return `${r.to}`;
  return '';
};

const GenerationHandoffCard: React.FC = () => {
  if (!isGenerationHandoffEnabled()) return null;

  const generations = useMemo<LegacyCollection[]>(
    () => buildGenerationCollection(6),
    [],
  );

  const [active, setActive] = useState<LegacyCollection | null>(null);
  const [playOpen, setPlayOpen] = useState(false);

  const story = useMemo<PlaybackStory | null>(() => {
    if (!active) return null;
    if (!isStoryPlaybackEnabled()) return null;
    const slides = collectionToPlaybackSlides(active, 14);
    if (!slides.length) return null;
    return {
      label: active.title,
      subtitle: active.citation,
      slides,
    };
  }, [active]);

  if (!generations.length) return null;

  return (
    <section className="py-12 px-4">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-2 mb-4">
          <GitBranch className="w-5 h-5 text-emerald-600" />
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-slate-900">
            Across the Generations
          </h2>
          <span className="text-[10px] uppercase tracking-wide font-semibold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">
            Beta
          </span>
        </div>
        <p className="text-sm text-slate-600 max-w-2xl mb-6">
          Traditions and recurring moments your family has carried forward —
          across at least 20 years of saved memories. Nothing is inferred;
          every line below is grounded in real moments you've recorded.
        </p>

        <div className="space-y-4">
          {generations.map((g) => {
            const range = formatYearRange(g.yearRange);
            const span =
              g.yearRange.from && g.yearRange.to ? g.yearRange.to - g.yearRange.from : 0;
            const startedYear = g.yearRange.from;
            const stillActive =
              g.yearRange.to && g.yearRange.to >= new Date().getFullYear() - 5;
            return (
              <Card
                key={g.id}
                className="overflow-hidden border border-slate-100 shadow-sm"
              >
                <div className="bg-gradient-to-br from-emerald-50 via-teal-50 to-sky-50 p-5 md:p-6 grid md:grid-cols-[1fr_auto] gap-4 items-center">
                  <div className="space-y-2">
                    <h3 className="font-serif text-xl md:text-2xl font-bold text-slate-900 leading-tight">
                      {g.title}
                    </h3>
                    <div className="flex flex-wrap items-center gap-3 text-sm text-slate-700">
                      {startedYear && (
                        <div className="inline-flex items-center gap-1.5">
                          <Sparkles className="w-3.5 h-3.5 text-emerald-600" />
                          <span>
                            <span className="font-semibold">Started with</span>{' '}
                            {startedYear}
                          </span>
                        </div>
                      )}
                      {range && (
                        <div className="inline-flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5 text-teal-600" />
                          {range} · {span}+ year span
                        </div>
                      )}
                      {stillActive && (
                        <Badge className="bg-emerald-600 hover:bg-emerald-600 text-white text-[10px] uppercase">
                          Still active today
                        </Badge>
                      )}
                    </div>
                    {g.topPeople.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 pt-1">
                        {g.topPeople.slice(0, 6).map((p) => (
                          <Badge
                            key={p}
                            variant="outline"
                            className="text-[11px] capitalize"
                          >
                            {p}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex md:flex-col gap-2">
                    <Button
                      size="sm"
                      onClick={() => {
                        setActive(g);
                        if (isStoryPlaybackEnabled()) setPlayOpen(true);
                      }}
                      disabled={!isStoryPlaybackEnabled()}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      title={
                        isStoryPlaybackEnabled()
                          ? 'Play this lineage'
                          : 'Story playback is disabled'
                      }
                    >
                      <PlayCircle className="w-4 h-4 mr-1.5" />
                      Play lineage
                    </Button>
                  </div>
                </div>
                <CardContent className="bg-white px-5 md:px-6 py-3 text-[11px] italic text-slate-500 border-t border-slate-100">
                  {g.citation}
                </CardContent>
              </Card>
            );
          })}
        </div>

        <p className="text-[11px] text-slate-400 mt-6 text-center">
          We never infer relationships, emotions, or events that aren't in
          your saved memories. Every entry is read-only, anchored, and cited.
        </p>
      </div>

      <StoryPlaybackModal
        open={playOpen}
        onOpenChange={setPlayOpen}
        story={story}
      />
    </section>
  );
};

export default GenerationHandoffCard;
